// Portal v2 Slice F — Messages (2026-08-30). One page, two modes:
// /messages (thread list + new-message composer) and /messages/:id
// (thread detail + reply). Current visual language; the reskin is C2.
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ApiError } from "@/lib/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createPortalThread,
  fetchPortalThread,
  fetchPortalThreads,
  replyPortalThread,
} from "@/lib/portal-messages";
import { formatDate } from "@/lib/portal-orders";
import { useAuth } from "@/auth/AuthProvider";
import { PortalLayout } from "@/components/PortalLayout";

export function MessagesPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  if (!user) return null;
  return (
    <PortalLayout>
      {/* key remounts the detail (and its draft state) per thread — a
          draft must never follow the reader to another thread. */}
      {id ? <ThreadDetail key={id} threadId={id} /> : <ThreadList />}
    </PortalLayout>
  );
}

function ThreadList() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data, isPending, isError } = useQuery({
    queryKey: ["portal-threads"],
    queryFn: fetchPortalThreads,
  });
  const [composerOpen, setComposerOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const createMutation = useMutation({
    mutationFn: () =>
      createPortalThread({ subject: subject.trim() || undefined, body: body.trim() }),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ["portal-threads"] });
      navigate(`/messages/${res.thread_id}`);
    },
  });

  const threads = data?.threads ?? [];
  return (
    <>
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-display text-3xl font-bold tracking-tight">
          Messages
        </h1>
        {!composerOpen && (
          <button
            type="button"
            onClick={() => setComposerOpen(true)}
            className="rounded-md bg-ink px-5 py-2.5 font-semibold text-white shadow-sm transition-colors hover:bg-ink-700"
          >
            New message
          </button>
        )}
      </div>

      {composerOpen && (
        <section className="mb-8 rounded-lg border border-cream-200 bg-white p-6 shadow-sm">
          <h2 className="mb-4 font-display text-lg font-bold tracking-tight">
            Message your account manager
          </h2>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={200}
            placeholder="Subject (optional — an order number helps, e.g. Order #60018)"
            className="mb-3 w-full rounded-md border border-cream-300 bg-white p-2.5 text-sm text-brown-500 focus:border-peach-400 focus:outline-none"
          />
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={4000}
            rows={4}
            placeholder="What can we help with?"
            className="mb-3 w-full rounded-md border border-cream-300 bg-white p-2.5 text-sm text-brown-500 focus:border-peach-400 focus:outline-none"
          />
          {createMutation.isError && (
            <p className="mb-3 text-sm text-rust-500">
              Something went wrong. Try again in a moment.
            </p>
          )}
          <div className="flex gap-3">
            <button
              type="button"
              disabled={body.trim().length === 0 || createMutation.isPending}
              onClick={() => createMutation.mutate()}
              className="rounded-md bg-ink px-5 py-2 font-semibold text-white transition-colors hover:bg-ink-700 disabled:opacity-50"
            >
              {createMutation.isPending ? "Sending…" : "Send"}
            </button>
            <button
              type="button"
              disabled={createMutation.isPending}
              onClick={() => setComposerOpen(false)}
              className="rounded-md border border-cream-300 bg-cream-50 px-5 py-2 font-semibold text-brown-500 transition-colors hover:bg-cream-100"
            >
              Cancel
            </button>
          </div>
          <p className="mt-3 text-sm text-brown-400">
            We'll notify your account manager as soon as you send.
          </p>
        </section>
      )}

      {isPending ? (
        <div className="h-24 animate-pulse rounded-lg border border-cream-200 bg-cream-100" />
      ) : isError ? (
        <div className="rounded-lg border border-cream-200 bg-white px-6 py-16 text-center text-brown-400 shadow-sm">
          Something went wrong loading your messages — refresh to try again.
        </div>
      ) : threads.length === 0 ? (
        <div className="rounded-lg border border-cream-200 bg-white px-6 py-16 text-center shadow-sm">
          <p className="mb-2 font-display text-xl font-bold tracking-tight">
            No messages yet
          </p>
          <p className="text-brown-400">
            Questions about an order, artwork, or anything else — start a
            message and your account manager will get back to you.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {threads.map((t) => (
            <Link
              key={t.id}
              to={`/messages/${t.id}`}
              className="flex items-center gap-4 rounded-lg border border-cream-200 bg-white px-5 py-4 shadow-sm transition-colors hover:border-peach-300"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-semibold text-brown-500">
                    {t.subject ?? t.projectName ?? "General"}
                  </span>
                  {t.unreadCount > 0 && (
                    <span className="rounded-full bg-ink px-2 py-0.5 text-xs font-bold text-white tabular-nums">
                      {t.unreadCount}
                    </span>
                  )}
                </div>
                {t.projectName && t.subject && (
                  <div className="truncate text-sm text-brown-400">
                    {t.projectName}
                  </div>
                )}
              </div>
              <span className="text-sm text-brown-300">
                {formatDate(t.lastMessageAt)}
              </span>
            </Link>
          ))}
        </div>
      )}
    </>
  );
}

function ThreadDetail({ threadId }: { threadId: string }) {
  const queryClient = useQueryClient();
  const { data, isPending, isError, error } = useQuery({
    queryKey: ["portal-thread", threadId],
    queryFn: () => fetchPortalThread(threadId),
  });
  // Opening the thread stamped the read cursor server-side — the list's
  // unread badge must not keep claiming otherwise.
  useEffect(() => {
    if (data) {
      void queryClient.invalidateQueries({ queryKey: ["portal-threads"] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id]);
  const notFound = isError && error instanceof ApiError && error.status === 404;
  const [reply, setReply] = useState("");
  const replyMutation = useMutation({
    mutationFn: () => replyPortalThread(threadId, reply.trim()),
    onSuccess: () => {
      setReply("");
      void queryClient.invalidateQueries({ queryKey: ["portal-thread", threadId] });
      void queryClient.invalidateQueries({ queryKey: ["portal-threads"] });
    },
  });

  return (
    <>
      <Link
        to="/messages"
        className="mb-6 inline-block text-sm font-semibold text-ink underline-offset-2 hover:underline"
      >
        ← All messages
      </Link>
      {isPending ? (
        <div className="h-40 animate-pulse rounded-lg border border-cream-200 bg-cream-100" />
      ) : notFound ? (
        <div className="rounded-lg border border-cream-200 bg-white px-6 py-16 text-center text-brown-400 shadow-sm">
          This conversation is no longer available.
        </div>
      ) : isError || !data ? (
        <div className="rounded-lg border border-cream-200 bg-white px-6 py-16 text-center text-brown-400 shadow-sm">
          Something went wrong loading this conversation — refresh to try
          again.
        </div>
      ) : (
        <>
          <h1 className="mb-1 font-display text-2xl font-bold tracking-tight">
            {data.subject ?? data.projectName ?? "General"}
          </h1>
          {data.projectName && (
            <p className="mb-6 text-sm text-brown-400">{data.projectName}</p>
          )}
          <div className="mb-6 space-y-3">
            {data.messages.map((m) => (
              <div
                key={m.id}
                className={
                  "max-w-[85%] rounded-lg border px-4 py-3 shadow-sm " +
                  (m.authorSide === "client"
                    ? "ml-auto border-peach-300 bg-peach-50"
                    : "border-cream-200 bg-white")
                }
              >
                <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-brown-300">
                  {m.authorSide === "client"
                    ? "You"
                    : (m.authorName ?? "Your account team")}{" "}
                  · {formatDate(m.createdAt)}
                </div>
                <p className="whitespace-pre-wrap text-sm text-brown-500">
                  {m.body}
                </p>
              </div>
            ))}
          </div>
          <div className="rounded-lg border border-cream-200 bg-white p-4 shadow-sm">
            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              maxLength={4000}
              rows={3}
              placeholder="Write a reply…"
              className="mb-3 w-full rounded-md border border-cream-300 bg-white p-2.5 text-sm text-brown-500 focus:border-peach-400 focus:outline-none"
            />
            {replyMutation.isError && (
              <p className="mb-3 text-sm text-rust-500">
                Something went wrong. Try again in a moment.
              </p>
            )}
            <button
              type="button"
              disabled={reply.trim().length === 0 || replyMutation.isPending}
              onClick={() => replyMutation.mutate()}
              className="rounded-md bg-ink px-5 py-2 font-semibold text-white transition-colors hover:bg-ink-700 disabled:opacity-50"
            >
              {replyMutation.isPending ? "Sending…" : "Send reply"}
            </button>
          </div>
        </>
      )}
    </>
  );
}
